import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

import { fetchNutritionList } from '../optionsSlice';

// Async thunk to fetch ingredients data from API
export const fetchIngredients = createAsyncThunk(
  'ingredients/fetchIngredients',
  async ({ page = 1, searchTerm = "" }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/nutritions-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch ingredients');
    }
  }
);

// Async thunk to delete an ingredient
export const deleteIngredient = createAsyncThunk(
  'ingredients/deleteIngredient',
  async (ingredientId, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/nutritions-delete`, { id: ingredientId });
      if (response.data.success) {
        // Dispatch fetchIngredients with the current page
        dispatch(fetchIngredients({ page: 1 })); // Change to the correct page if needed
        dispatch(fetchNutritionList());
      }
      return ingredientId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete ingredient');
    }
  }
);

// Async thunk to create a new ingredient
export const createIngredient = createAsyncThunk(
  'ingredients/createIngredient',
  async (ingredientData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/nutritions`, ingredientData);
      if (response.data.success) {
        dispatch(fetchIngredients({ page: 1 })); // Refetch the ingredients list
        dispatch(fetchNutritionList());
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create ingredient');
    }
  }
);

// Async thunk to update an existing ingredient
export const updateIngredient = createAsyncThunk(
  'ingredients/updateIngredient',
  async (ingredientData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/nutritions/${ingredientData.id}`, ingredientData);
      if (response.data.success) {
        dispatch(fetchIngredients({ page: ingredientData.page }));
        dispatch(fetchNutritionList());
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update ingredient');
    }
  }
);

const ingredientsSlice = createSlice({
  name: 'admin_ingredients',
  initialState: {
    ingredients: [],
    currentIngredientPage: 1,
    totalIngredientPages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    setIngredientPage: (state, action) => {
      state.currentIngredientPage = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIngredients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIngredients.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = action.payload.data.data;
        state.currentIngredientPage = action.payload.data.current_page || 1;
        state.totalIngredientPages = action.payload.data.last_page || 0;
      })
      .addCase(fetchIngredients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteIngredient.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteIngredient.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = state.ingredients.filter(ingredient => ingredient.id !== action.payload);
        state.successMessage = 'Ingredient deleted successfully'; // Success message for deletion
      })
      .addCase(deleteIngredient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createIngredient.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.ingredients.push(action.payload.data); // Assuming data contains the created ingredient
          state.successMessage = 'Ingredient created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createIngredient.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(updateIngredient.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.ingredients.findIndex(ingredient => ingredient.id === action.payload.data.id);
          if (index !== -1) {
            state.ingredients[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Ingredient updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateIngredient.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      });
  },
});

export const { setIngredientPage, clearMessages } = ingredientsSlice.actions;

export default ingredientsSlice.reducer;